import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { ArrowUpRight, Clock, Quote } from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHw0fHxleGVjdXRpdmUlMjBjb2FjaGluZyUyMHByb2Zlc3Npb25hbCUyMHBvcnRyYWl0fGVufDB8fHx8MTc4MzQ3MDAzMXww&ixlib=rb-4.1.0&q=85";
const WORK_IMG = "https://images.unsplash.com/photo-1601642964568-1917224f4e4d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBvcmdhbml6ZWQlMjBkZXNrJTIwd29ya3NwYWNlJTIwbWluaW1hbGlzdHxlbnwwfHx8fDE3ODM0NzAwMzF8MA&ixlib=rb-4.1.0&q=85";

const APPROACH = [
  { n: "01", t: "Listen deeply", d: "We start where you are. Every engagement opens with a real conversation about your goals, pressures, and definition of success." },
  { n: "02", t: "Design the system", d: "Together we build the routines, calendars, and decision frameworks that turn intention into consistent action." },
  { n: "03", t: "Stay accountable", d: "Ongoing sessions keep momentum alive — recalibrating as life shifts, so progress compounds week over week." },
];

const VOICES = [
  { q: "Zenith gave me back ten hours a week and a calmer mind. I finally run my calendar instead of it running me.", a: "Marcus L.", r: "Startup Founder" },
  { q: "The coaching was transformative — grounded, practical, and never preachy. I show up to my life differently now.", a: "Priya S.", r: "Creative Director" },
  { q: "Having a personal assistant setup tailored to how I actually think changed everything about my week.", a: "Dana R.", r: "Consultant" },
];

export default function Landing() {
  const navigate = useNavigate();
  const [services, setServices] = useState([]);

  useEffect(() => {
    api.get("/services").then(({ data }) => setServices(data)).catch(() => {});
    if (window.location.hash) {
      const el = document.querySelector(window.location.hash);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 200);
    }
  }, []);

  return (
    <div className="bg-cream min-h-screen" data-testid="landing-page">
      <Navbar />

      {/* Hero */}
      <section className="pt-36 md:pt-44 pb-24 md:pb-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: "easeOut" }}
            className="md:col-span-7">
            <p className="overline text-clay mb-6" data-testid="hero-overline">Coaching · Consulting · Personal Assistance</p>
            <h1 className="font-serif font-light text-5xl md:text-7xl leading-[0.95] tracking-tight text-forest">
              Clarity is a<br /><span className="italic text-clay">practice</span>, not a moment.
            </h1>
            <p className="text-lg text-muted-foreground max-w-md mt-8 leading-relaxed">
              Personal coaching and hands-on assistance for people carrying too much. Book a session and reclaim your focus, your time, and your direction.
            </p>
            <div className="flex flex-wrap items-center gap-4 mt-10">
              <Button onClick={() => navigate("/book")} className="rounded-none bg-forest hover:bg-forest-dark text-cream px-8 py-6 text-base group transition-colors duration-300" data-testid="hero-book-btn">
                Book your session
                <ArrowUpRight className="w-4 h-4 ml-2 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-300" />
              </Button>
              <button onClick={() => document.querySelector("#services")?.scrollIntoView({ behavior: "smooth" })} className="text-forest underline underline-offset-4 hover:text-clay transition-colors duration-300" data-testid="hero-explore-btn">
                Explore services
              </button>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1, ease: "easeOut", delay: 0.2 }}
            className="md:col-span-5 relative">
            <div className="absolute -top-6 -left-6 w-32 h-32 border border-clay/40 hidden md:block" />
            <img src={HERO_IMG} alt="Coach portrait" className="w-full h-[420px] md:h-[560px] object-cover grayscale-[15%]" />
            <div className="absolute -bottom-6 -right-6 bg-clay text-cream px-6 py-4">
              <p className="font-serif text-3xl leading-none">12+ yrs</p>
              <p className="text-xs mt-1 tracking-wide">guiding leaders</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services */}
      <section id="services" className="py-24 md:py-32 bg-surfacealt scroll-mt-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
            <div>
              <p className="overline text-clay mb-4">Ways to work together</p>
              <h2 className="font-serif font-light text-4xl md:text-5xl text-forest leading-none">Choose your entry point</h2>
            </div>
            <p className="text-muted-foreground max-w-sm">Every session is one-to-one. Start small with a complimentary call or dive straight into strategy.</p>
          </div>

          <div className="grid md:grid-cols-2 gap-px bg-border">
            {services.map((s, i) => (
              <motion.div key={s.id}
                initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: i * 0.08 }}
                className="bg-cream p-8 md:p-12 group hover:bg-white transition-colors duration-300 cursor-pointer"
                onClick={() => navigate(`/book?service=${s.id}`)}
                data-testid={`service-card-${s.id}`}>
                <div className="flex items-start justify-between mb-6">
                  <span className="font-serif text-5xl text-clay/30 group-hover:text-clay transition-colors duration-300">0{i + 1}</span>
                  <span className="text-forest font-serif text-2xl">{s.price === 0 ? "Free" : `$${s.price}`}</span>
                </div>
                <h3 className="font-serif text-3xl text-forest mb-2">{s.name}</h3>
                <p className="flex items-center gap-2 text-sm text-clay mb-4"><Clock className="w-4 h-4" /> {s.duration} minutes</p>
                <p className="text-muted-foreground leading-relaxed mb-6">{s.description}</p>
                <span className="inline-flex items-center text-forest text-sm group-hover:text-clay transition-colors duration-300">
                  Book this <ArrowUpRight className="w-4 h-4 ml-1 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-300" />
                </span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Approach */}
      <section id="approach" className="py-24 md:py-32 scroll-mt-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-5 relative">
            <img src={WORK_IMG} alt="Organized workspace" className="w-full h-[440px] object-cover" />
          </div>
          <div className="md:col-span-6 md:col-start-7">
            <p className="overline text-clay mb-4">The approach</p>
            <h2 className="font-serif font-light text-4xl md:text-5xl text-forest leading-none mb-12">A calm, deliberate method</h2>
            <div className="space-y-10">
              {APPROACH.map((a) => (
                <div key={a.n} className="flex gap-6" data-testid={`approach-${a.n}`}>
                  <span className="font-serif text-3xl text-clay shrink-0">{a.n}</span>
                  <div>
                    <h3 className="font-serif text-2xl text-forest mb-2">{a.t}</h3>
                    <p className="text-muted-foreground leading-relaxed">{a.d}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Voices */}
      <section id="voices" className="py-24 md:py-32 bg-forest text-cream scroll-mt-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="overline text-clay mb-4">Voices</p>
          <h2 className="font-serif font-light text-4xl md:text-5xl leading-none mb-16">What clients carry forward</h2>
          <div className="grid md:grid-cols-3 gap-px bg-cream/10">
            {VOICES.map((v, i) => (
              <div key={i} className="bg-forest p-8 md:p-10" data-testid={`voice-${i}`}>
                <Quote className="w-8 h-8 text-clay mb-6" />
                <p className="font-serif text-xl leading-snug text-cream/90 mb-8">"{v.q}"</p>
                <p className="text-cream font-medium">{v.a}</p>
                <p className="text-cream/50 text-sm">{v.r}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 text-center">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="font-serif font-light text-4xl md:text-6xl text-forest leading-tight mb-8">
            The first step is a<br /><span className="italic text-clay">conversation.</span>
          </h2>
          <Button onClick={() => navigate("/book")} className="rounded-none bg-clay hover:bg-clay-dark text-cream px-10 py-6 text-base transition-colors duration-300" data-testid="cta-book-btn">
            Book a session
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
